<template>
  <div>
    <!-- Slot for the element that triggers the tooltip -->
    <div class="tooltip-trigger" @mouseenter="showTooltip" @mouseleave="hideTooltip" @focus="showTooltip"
      @blur="hideTooltip" ref="trigger">
      <slot></slot>
    </div>

    <!-- Tooltip element -->
    <transition name="tooltip" @enter="onEnter" @leave="onLeave">
      <div v-if="isVisible" class="material-tooltip" :class="[position, type]" :style="tooltipStyles" ref="tooltip"
        role="tooltip" :aria-hidden="!isVisible">
        <span class="tooltip-text">{{ text }}</span>
        <div class="tooltip-arrow" :style="arrowStyles"></div>
      </div>
    </transition>
  </div>
</template>

<script>
export default {
  name: 'Tooltip',
  props: {
    text: {
      type: String,
      required: true
    },
    position: {
      type: String,
      default: 'top',
      validator: (value) => ['top', 'bottom', 'left', 'right'].includes(value)
    },
    delay: {
      type: Number,
      default: 0
    },
    type: {
      type: String,
      default: 'default',
      validator: (value) => ['default', 'rich', 'error', 'success'].includes(value)
    },
    maxWidth: {
      type: String,
      default: '200px'
    }
  },
  data() {
    return {
      isVisible: false,
      tooltipStyles: {},
      arrowStyles: {},
      timeout: null
    };
  },
  methods: {
    showTooltip() {
      // Clear any existing timeout
      if (this.timeout) {
        clearTimeout(this.timeout);
      }

      // Set timeout for delay
      this.timeout = setTimeout(() => {
        this.isVisible = true;
        this.$nextTick(() => {
          this.calculatePosition();
        });
      }, this.delay);
    },

    hideTooltip() {
      if (this.timeout) {
        clearTimeout(this.timeout);
      }
      this.isVisible = false;
    },

    calculatePosition() {
      if (!this.$refs.trigger || !this.$refs.tooltip) return;

      const triggerRect = this.$refs.trigger.getBoundingClientRect();
      const tooltipRect = this.$refs.tooltip.getBoundingClientRect();

      let top = 0;
      let left = 0;
      let arrowPosition = {};

      top = triggerRect.top - 60;
      left = triggerRect.left - 65;
      arrowPosition = {
        transform: 'translateX(-50%)'
      };

      // Keep tooltip within viewport
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      if (left < 8) left = 8;
      if (left + tooltipRect.width > viewportWidth - 8) {
        left = viewportWidth - tooltipRect.width - 8;
      }
      if (top < 8) top = 8;
      if (top + tooltipRect.height > viewportHeight - 8) {
        top = viewportHeight - tooltipRect.height - 8;
      }

      this.tooltipStyles = {
        top: `${top}px`,
        left: `${left}px`,
        maxWidth: this.maxWidth
      };

      this.arrowStyles = arrowPosition;
    },

    onEnter(el) {
      el.style.opacity = 0;
      el.style.transform = 'scale(0.8)';

      requestAnimationFrame(() => {
        el.style.transition = 'opacity 0.2s ease, transform 0.2s ease';
        el.style.opacity = 1;
        el.style.transform = 'scale(1)';
      });
    },

    onLeave(el) {
      el.style.opacity = 0;
      el.style.transform = 'scale(0.8)';
    },

    handleResize() {
      if (this.isVisible) {
        this.calculatePosition();
      }
    }
  },

  mounted() {
    window.addEventListener('resize', this.handleResize);
    window.addEventListener('scroll', this.handleResize);
  },

  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize);
    window.removeEventListener('scroll', this.handleResize);
    if (this.timeout) {
      clearTimeout(this.timeout);
    }
  }
};
</script>

<style scoped>
.material-tooltip-container {
  display: inline-block;
  position: relative;
}

.tooltip-trigger {
  display: inline-block;
}

.material-tooltip {
  position: fixed;
  z-index: 1000;
  padding: 8px 12px;
  border-radius: 4px;
  font-family: 'Roboto', sans-serif;
  font-size: 12px;
  font-weight: 400;
  line-height: 1.4;
  text-align: center;
  pointer-events: none;
  white-space: normal;
  word-wrap: break-word;
  box-shadow: 0 3px 6px rgba(0, 0, 0, 0.16), 0 3px 6px rgba(0, 0, 0, 0.23);
}

/* Default theme */
.material-tooltip.default {
  background-color: #424242;
  color: white;
}

.material-tooltip.default .tooltip-arrow {
  border-color: #424242 transparent transparent transparent;
}

/* Rich theme */
.material-tooltip.rich {
  background-color: #f5f5f5;
  color: #424242;
  border: 1px solid #e0e0e0;
}

.material-tooltip.rich .tooltip-arrow {
  border-color: #e0e0e0 transparent transparent transparent;
}

/* Error theme */
.material-tooltip.error {
  background-color: #f44336;
  color: white;
}

.material-tooltip.error .tooltip-arrow {
  border-color: #f44336 transparent transparent transparent;
}

/* Success theme */
.material-tooltip.success {
  background-color: #4caf50;
  color: white;
}

.material-tooltip.success .tooltip-arrow {
  border-color: #4caf50 transparent transparent transparent;
}

.tooltip-arrow {
  position: absolute;
  width: 0;
  height: 0;
  border-style: solid;
  border-width: 6px 6px 0 6px;
}

.tooltip-text {
  display: block;
}

/* Transition styles */
.tooltip-enter-active,
.tooltip-leave-active {
  transition: opacity 0.2s ease, transform 0.2s ease;
}

.tooltip-enter,
.tooltip-leave-to {
  opacity: 0;
  transform: scale(0.8);
}

/* Responsive adjustments */
@media (max-width: 480px) {
  .material-tooltip {
    font-size: 11px;
    padding: 6px 10px;
    max-width: 150px !important;
  }
}
</style>


// WEBPACK FOOTER //
// Tooltip.vue?3cb6d6f1